<template>
	<ion-page>
		<A-header 
			v-model="searchQuery" 
			:title="title" 
			:hasSearch="hasSearch" 
			:searchActive="searchActive"
			:backArrow="backArrow" 
			:addAction="finalAddAction" 
			@enterPressed="searchFirst()"
			@toggleSearch="toggleSearch" 
			ref="header" 
		/>

		<div v-if="error"
			class="bg-light w-full h-full flex flex-col ion-justify-content-center ion-align-items-center">
			<img src="@/plugins/app/_assets/error.svg" />
			<h3 class="mt-2 mr-2 ml-2 text-center">
				Pri načítaní dát nastala chyba, skúste to znova.
			</h3>
			<p class="mt-0 mb-4">
				{{ error?.status ? `Code ${error.status}` : error.message ?? error }}
			</p>
			<ion-button color="primary" @click="refresh()">Skúsiť znova</ion-button>
		</div>

		<template v-else>
			<slot name="subheader"></slot>

			<a-capacity-banner v-if="bannerShowed" :detail="capacityWarning" :type="capacityBannerType"/>

			<ion-content :scroll-y="lockYScrolling" scrollEvents @ionScroll="contentScrolled" ref="content">
				<slot></slot>
				<ion-modal class="setCapacityModal" ref="modal" :is-open="modalOpened">
					<ion-content style="margin: 10px;" class="ion-padding">
						<div>
							<div>
								<div class="flex justify-between">
									<div class="icon-container">
										<ion-icon class="size-xl modal-icon" :icon="$icons.warning" />
									</div>
									<div @click="closeModal()">
										<ion-icon  class="size-xl cursor-pointer opacity-25" :icon="$icons.closeOutline" />
									</div>
								</div>


								<h1 class="modal-title">Nemáš nastavené kapacity</h1>
								<p class="modal-text">
									Pomôž nám lepšie plánovať tím, že si nastavíš kapacity na {{ capacityWarning }}. Zaberie ti to len chvíľu a veľmi nám to uľahčí organizáciu práce.
								</p>
							</div>
						</div>
						<div class="modal-buttons">
							<ion-button class="cancel-button" @click="closeModal()">Nastavím si neskôr</ion-button>
							<ion-button class="set-button" @click="setCapacity()">Nastaviť kapacity</ion-button>
						</div>
					</ion-content>
				</ion-modal>

				<Search-overlay v-if="searchActive && hasSearchOverlay" :searchCache="searchCache"
					:initialProjects="projects" :searchQuery="searchQuery" :showTasks="showTasks" :url="searchUrl"
					:initialLimit="20" @projectClicked="searchProject" @taskClicked="searchTask" ref="searchOverlay" />
			</ion-content>
		</template>

		<Tracking-task-footer 
			v-show="getActiveTask" 
			@openTaskModal="doOpenTaskModal" 
			@clicked="doOpenTaskModal()"
			ref="footer"
			/>
	</ion-page>
</template>

<script>
import AHeader from '@/plugins/app/_layout/a-header.vue'
import TrackingTaskFooter from '@/plugins/lib@home/_components/tracking-task-footer.vue'
import ACapacityBanner from './a-capacity-banner.vue'

import { IonButton, IonModal, IonContent } from '@ionic/vue'

import CreateTaskModal from '@/plugins/lib@task/components/modals/modal-create-task.vue'
import ModalTask from '@/plugins/lib@task/components/modals/modal-task.vue'

import { IonIcon } from '@ionic/vue'

import { mapGetters, mapActions } from 'vuex'
import { onMounted, beforeUnmount, onUnmounted, getCurrentInstance, watch, ref } from 'vue'
import { useCheckedCapacity } from '@/plugins/app/use/useCheckedCapacity.js'

export default {
	props: {
		title: {
			type: String,
		},

		hasSearch: {
			type: Boolean,
			default: true,
		},

		hasSearchOverlay: {
			type: Boolean,
			default: false,
		},

		searchCache: {
			type: Object,
			default: () => ({}),
		},

		searchUrl: {
			type: String,
			default: '/search',
		},

		projects: {
			type: Array,
		},

		openTaskModal: {
			type: Function,
		},

		showTasks: {
			type: Boolean,
			default: true,
		},

		lockYScrolling: {
			type: Boolean,
			default: true,
		},

		backArrow: {
			type: Boolean,
			default: false,
		},

		addAction: {
			type: Function,
		},
	},

	components: { AHeader, TrackingTaskFooter, ACapacityBanner },

	emits: [
		'searchTask',
		'searchProject',
		'searchToggled',
		'refresh',
		'contentScroll',
	],

	computed: {
		...mapGetters('projectsStore', ['getActiveTask']),
		...mapGetters('projectsStore', ['getUser']),

		finalAddAction() {
			return this.addAction === undefined
				? this.openCreateTaskModal
				: this.addAction
		},
	},

	data() {
		return {
			searchQuery: '',
			searchActive: false,
			error: false,
		}
	},

	async mounted() {
		await this._updateState()

		window.onkeydown = (e) => {
			const isSearchShortcut =
				(this.hasSearch && (e.metaKey || e.ctrlKey) && e.shiftKey && e.key == 'F') ||
				(this.hasSearch && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() == 'k')
			if (isSearchShortcut) {
				e.preventDefault()
				this.toggleSearch(true)
			}
		}

		window.onfocus = async () => {
			await this._updateState()
			this.$emit('refresh')
		}
	},

	methods: {
		// ----- Setup -----
		...mapActions('projectsStore', ['updateTrackingState']),

		async _updateState() {
			try {
				await this.updateTrackingState()
				this.error = false
			} catch (err) {
				console.error('State update failed:', (this.error = err))
			}
		},

		refresh() {
			try {
				window.location.reload()
			} catch (e) {
				this.$router.go(0)
			}
		},

		hideSearch() {
			this.$refs['header'].hideSearchbar()
		},

		toggleSearch(isActive, query) {
			if ((this.searchActive = isActive)) this.$refs.header.focusSearch()

			if (!isActive && this.$route.query.search !== undefined)
				this.$router.push(this.$route.path)

			if (query) this.searchQuery = query

			this.$emit('searchToggled', this.searchQuery, isActive)
		},

		/** @public */
		isHidden() {
			return this.$el?.classList.contains('ion-page-hidden')
		},

		searchFirst() {
			this.$refs.searchOverlay.clickFirstProject()
		},

		async searchTask(task, project) {
			this.hideSearch()
			this.$emit('searchTask', task, project)
		},

		async searchProject(project) {
			this.hideSearch()
			this.$emit('searchProject', project)
		},

		contentScrolled(ev) {
			this.$emit('contentScroll', ev)
		},

		async doOpenTaskModal(task, isUnassigned) {
			task ??= this.getActiveTask

			const isTaskModalOpen = this.openTaskModal && !(await this.openTaskModal(task, isUnassigned))
			if (isTaskModalOpen) return

			try {
				this.$modals.open(ModalTask, { task, isUnassigned })
			} catch (err) {
				console.error(err)
			}
		},

		async openCreateTaskModal() {
			this.$modals.open(CreateTaskModal)
		},
	},

	watch: {
		$route: {
			handler(route) {
				this.$nextTick(() =>
					this.toggleSearch(route.query.search !== undefined, route.query.search)
				)
			},
			immediate: true,
		},
	},

	setup() {
		const { isNextMonthChecked, checkedCapacity, getCapacityInformation, capacityBannerType } = useCheckedCapacity()
		const { appContext } = getCurrentInstance()
		const bus = appContext.config.globalProperties.$eventBus
		const router = appContext.config.globalProperties.$router

		const modalOpened = ref(false)
		const bannerShowed = ref(!!JSON.parse(localStorage.getItem('bannerShowed')))
		const capacityWarning = ref(localStorage.getItem('capacityWarning') ?? '')
		const storedIso = localStorage.getItem('dateWhenModalClosed')
		const storageIsModalOpened = JSON.parse(localStorage.getItem('isModalOpened') ?? 'null')

		onMounted(() => {
			bus.on('capacityUpdated', handleCapacityUpdated)
		})
		onUnmounted(() => {
			bus.off('capacityUpdated', handleCapacityUpdated)
		})

		async function handleCapacityUpdated() {
			await getCapacityInformation()
			setCapacityWarning()
			localStorage.setItem('bannerOpened', JSON.stringify(bannerShowed.value))
		}

		// Funkcia na update banneru a uloženie do localStorage
		function setCapacityWarning() {
			//toto je logika cisto len na zobrazenie modalu
			if (!isNextMonthChecked.value || !checkedCapacity.value) {
				if (storedIso) {
					const storedDate = new Date(storedIso)
					const today = new Date()
				
					if (storedDate.toDateString() !== today.toDateString()) {
						modalOpened.value = true
					}
				}

				if (storageIsModalOpened == true || storageIsModalOpened == null) {
					modalOpened.value = true
				}
			}
		
			//toto uz je logika na zobrazenie banneru aj textu v nom/modali
			if (isNextMonthChecked.value && checkedCapacity.value) {
				bannerShowed.value = false
				capacityWarning.value = ''
			} else if (!isNextMonthChecked.value && !checkedCapacity.value) {
				bannerShowed.value = true
				capacityWarning.value = 'tento a aj na další mesiac'
			} else if (!isNextMonthChecked.value) {
				bannerShowed.value = true
				capacityWarning.value = 'na další mesiac'
			} else if (!checkedCapacity.value) {
				bannerShowed.value = true
				capacityWarning.value = 'tento mesiac'
			}
			// Ulož stav do localStorage
			localStorage.setItem('bannerShowed', JSON.stringify(bannerShowed.value))
			localStorage.setItem('capacityWarning', capacityWarning.value)
		}
		
		onMounted(() => {
			bannerShowed.value = JSON.parse(localStorage.getItem('bannerShowed')) ?? false
			capacityWarning.value = localStorage.getItem('capacityWarning') ?? ''
			getCapacityInformation()
		})

		watch(checkedCapacity, (newVal) => {
			if (!newVal) {
				setCapacityWarning()
			}
		})

		watch(isNextMonthChecked, (newVal) => {
			if (!newVal) {
				setCapacityWarning()
			}
		})

		const setCapacity = () => {
			modalOpened.value = false
			router.push({ name: 'capacity' })
			window.localStorage.setItem('isModalOpened', 'false')
			const now = new Date()
			window.localStorage.setItem('dateWhenModalClosed', now.toISOString())
		}

		const closeModal = () => {
			modalOpened.value = false
			window.localStorage.setItem('isModalOpened', 'false')
			const now = new Date()
			window.localStorage.setItem('dateWhenModalClosed', now.toISOString())
		}

		return {
			isNextMonthChecked,
			checkedCapacity,
			modalOpened,
			bannerShowed,
			capacityWarning,
			setCapacity,
			closeModal,
			getCapacityInformation,
			capacityBannerType
		}
	}
}
</script>

<style scoped>
.setCapacityModal {
	--border-radius: 12px;
	/* Rounded corners */
	--box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
	/* Light shadow for depth */
	--height: 250px;
	--width: 600px;
}


@media (max-width: 576px) {
	.setCapacityModal {
		--width: 95%;
		/* Make modal width 95% on small screens */
		--height: 40%;
	}
}

@media (min-width: 577px) {
	.setCapacityModal .modal-buttons {
		display: flex;
		justify-content: flex-end;
	}
}

.icon-container {
	background-color: rgba(255, 0, 0, 0.05);
	border-radius: 100%;
	width: 50px;
	height: 50px;
	display: flex;
	justify-content: center;
	align-items: center;
}

.modal-title {
	display: flex;
	font-size: 24px;
	text-align: left;
	font-family: Roboto;
	font-weight: 600;
}


.modal-text {
	color: #96989F;
	/* Light gray text */
	font-size: 16px;
	margin-top: 10px;
	text-align: left;
}

.modal-icon {
	color: rgba(255, 0, 0, 0.3);
}

.modal-buttons {
	display: flex;
	justify-content: center;
	height: max-content;
	align-items: end;
}

.cancel-button {
	margin-right: 10px;
	--background: transparent;
	--font-size: 14px;
	--color: #96989F;
	--border-radius: 5px;
	--font-weight: 600;
	--font-family: Roboto;

	/* Blue text for the first button */

}

.cancel-button:hover {
	--color: white;
}

.set-button {
	margin-left: 10px;
	--background: #216DE3;
	/* Blue background for the second button */
	--color: white;
	--border-radius: 5px;
}


.center-wrapper {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 10px;

}
</style>
